/* eslint-disable @nrwl/nx/enforce-module-boundaries */

/**
 * 产品模块 api
 */
import { API_PREFIX } from '@shared/config';
import type { FundListRequest, FundListResponse } from '@shared/types/httpTypes/applet/fund/list.do_post';
import type {
  ManualPreviewImgListRequest,
  ManualPreviewImgListResponse,
} from '@shared/types/httpTypes/applet/fund/manual/preview_img_list_get';
import type {
  PageFindDetailRequest,
  PageFindDetailResponse,
} from '@shared/types/httpTypes/applet/page/find_detail.do_post';
import type { PageFindListRequest, PageFindListResponse } from '@shared/types/httpTypes/applet/page/find_list.do_post';
import type { GetBrieflyRequest, GetBrieflyResponse } from '@shared/types/httpTypes/applet/product/get/briefly_get';
import type {
  ProductListByTypeIdRequest,
  ProductListByTypeIdResponse,
} from '@shared/types/httpTypes/applet/product/list_by_typeId.do_get';
import type {
  ProductListTypeRequest,
  ProductListTypeResponse,
} from '@shared/types/httpTypes/applet/product/list_type.do_get';
import type {
  ZxStaffBankOrgInfoGetBankOrgByStaffRequest,
  ZxStaffBankOrgInfoGetBankOrgByStaffResponse,
} from '@shared/types/httpTypes/applet/zxStaffBankOrgInfo/getBankOrgByStaff_post';
import type {
  ZxStaffBankOrgInfoSaveBankOrgInfoRequest,
  ZxStaffBankOrgInfoSaveBankOrgInfoResponse,
} from '@shared/types/httpTypes/applet/zxStaffBankOrgInfo/saveBankOrgInfo_post';
import { httpClient } from '@shared/utils';
import type { ApiResult } from '../apis.d';
import type {
  ProductDataCountGetProductDetailDataRequest,
  ProductDataCountGetProductDetailDataResponse,
} from '@shared/types/httpTypes/applet/productDataCount/getProductDetailData_get';

import {
  MfNetValueLatestWeeklyYieldsRequest,
  MfNetValueLatestWeeklyYieldsResponse,
} from '@shared/types/httpTypes/applet/mfNetValue/latest_weekly_yields_get';
import {
  MfNetValueRestoredCsi300RateStatisticRequest,
  MfNetValueRestoredCsi300RateStatisticResponse,
} from '@shared/types/httpTypes/applet/mf_net_value_restored/csi300_rate_statistic_get';
import type {
  ProductListProductColumnInfoRequest,
  ProductListProductColumnInfoResponse,
} from '@shared/types/httpTypes/applet/product/list_product_column_info_post';
import type {
  ListPosterRequest,
  ListPosterResponse,
} from '@shared/types/httpTypes/applet/product/material/list/poster_get';
import type { ListNewsRequest, ListNewsResponse } from '@shared/types/httpTypes/applet/product/material/list/news_get';
import type {
  ListVideoRequest,
  ListVideoResponse,
} from '@shared/types/httpTypes/applet/product/material/list/video_get';
import type { FundManualRequest, FundManualResponse } from '@shared/types/httpTypes/applet/fund/manual_get';
import type {
  VisiblePosterRequest,
  VisiblePosterResponse,
} from '@shared/types/httpTypes/applet/product/material/update/visible/poster_post';
import type {
  VisibleNewsRequest,
  VisibleNewsResponse,
} from '@shared/types/httpTypes/applet/product/material/update/visible/news_post';
import type {
  VisibleVideoRequest,
  VisibleVideoResponse,
} from '@shared/types/httpTypes/applet/product/material/update/visible/video_post';
import type {
  ProductListSpeechRequest,
  ProductListSpeechResponse,
} from '@shared/types/httpTypes/applet/product/list_speech.do_post';
import type {
  ProductListIssueRequest,
  ProductListIssueResponse,
} from '@shared/types/httpTypes/applet/product/list_issue.do_post';
import type { ConfigValueRequest, ConfigValueResponse } from '@shared/types/httpTypes/applet/product/config/value_post';
import type {
  IssueListRequest,
  IssueListResponse,
} from '@shared/types/httpTypes/applet/product/contact/issue/list_get';
import type {
  IntentionCustomerListCustomerRequest,
  IntentionCustomerListCustomerResponse,
} from '@shared/types/httpTypes/data/home/intention-customer/list-customer_post';

//获取产品详情
export const getProductDetailById = async (params?: any): Promise<ApiResult<any>> =>
  await httpClient.post(`${API_PREFIX}api/applet/product/get_detail.do`, params, {
    headers: {
      isHideError: true,
    },
  });
/**
 * 产品类别列表接口
 * https://yapi.tengmoney.com/project/59/interface/api/3337
 */
export const getProductCategoryListApi = async (params: ProductListTypeRequest): Promise<ProductListTypeResponse> => {
  return await httpClient.get(`${API_PREFIX}api/applet/product/list_type.do`, {
    params,
  });
};

/**
 * 通过产品类型列出所有的产品
 * https://yapi.tengmoney.com/project/13/interface/api/2672
 */
export const getProductListByCategoryIdApi = async (
  params: ProductListByTypeIdRequest
): Promise<ProductListByTypeIdResponse> => {
  return await httpClient.post(`${API_PREFIX}api/applet/product/list-type-products`, params);
};

/**
 * 按类型查询页面动态配置，本处用于查询基金类别
 * https://yapi.tengmoney.com/project/13/interface/api/2672
 * params.type 0 动态类型 1 访客行为 2动态通知设置 3员工号参数 4早报正文类别 5 陕西农行业绩 6 基金类别
 */
export const getFundCategoryListApi = async (params: PageFindListRequest): Promise<PageFindListResponse> => {
  return await httpClient.post(`${API_PREFIX}api/applet/page/find_list.do`, params);
};

/**
 * 按条件查询基金列表
 * https://yapi.tengmoney.com/project/13/interface/api/2052
 */
export const getFundListApi = async (params: FundListRequest): Promise<FundListResponse> => {
  return await httpClient.post(`${API_PREFIX}api/applet/fund/list.do`, params);
};

/**
 * 获取产品简要信息
 * https://yapi.tengmoney.com/project/13/interface/api/24817
 */
export const getBrieflyDetailApi = async (params: GetBrieflyRequest): Promise<GetBrieflyResponse> => {
  return await httpClient.get(`${API_PREFIX}api/applet/product/get/briefly`, {
    params,
  });
};

/**
 * 中信业绩归属-保存员工对应机构信息
 * https://yapi.tengmoney.com/project/13/interface/api/8827
 */
export const saveBankOrgInfoApi = async (
  params: ZxStaffBankOrgInfoSaveBankOrgInfoRequest
): Promise<ZxStaffBankOrgInfoSaveBankOrgInfoResponse> => {
  return await httpClient.post(`${API_PREFIX}api/applet/zxStaffBankOrgInfo/saveBankOrgInfo`, params, {
    headers: { isHideError: true },
  });
};

/**
 * 中信业绩归属-获取员工对应机构信息
 * https://yapi.tengmoney.com/project/13/interface/api/8823
 */
export const getBankOrgInfoApi = async (
  params: ZxStaffBankOrgInfoGetBankOrgByStaffRequest
): Promise<ZxStaffBankOrgInfoGetBankOrgByStaffResponse> => {
  return await httpClient.post(`${API_PREFIX}api/applet/zxStaffBankOrgInfo/getBankOrgByStaff`, params, {
    headers: { isHideError: true },
  });
};

/**
 * 获取产品详情外链用户自定义业绩归属参数
 * https://yapi.tengmoney.com/project/13/interface/api/1692
 */
export const getCustomParamsApi = async (params: PageFindDetailRequest): Promise<PageFindDetailResponse> => {
  return await httpClient.post(`${API_PREFIX}api/applet/page/find_detail.do`, params, {
    headers: { isHideError: true },
  });
};

/**
 * 光大销售适当性提示白名单
 */
export const getSaleWhiteListApi = async (): Promise<ApiResult<any>> => {
  return await httpClient.get(`${API_PREFIX}api/applet/poster/is_in_whiteList`);
};

/**
 * 查看企业的理财产品的收益率名称是否在需要显示优化的白名单中
 */
export const getFinancialProductIncomeConfigApi = async (params: any): Promise<ApiResult<any>> => {
  return await httpClient.post(`${API_PREFIX}api/applet/product/config/switch`, params);
};

/**
 * 营销中心-产品数据统计指标
 * https://yapi.tengmoney.com/project/13/interface/api/24861
 */
export const getProductDataApi = async (
  params: ProductDataCountGetProductDetailDataRequest
): Promise<ProductDataCountGetProductDetailDataResponse> => {
  return await httpClient.get(`${API_PREFIX}api/applet/productDataCount/getProductDetailData`, {
    params,
    headers: { isHideError: true },
  });
};

/**
 * 获取名片显示与否
 * https://yapi.tengmoney.com/project/13/interface/api/8211
 */
export const getShareCardShowApi = async (params: any): Promise<ApiResult<any>> => {
  return await httpClient.get(`${API_PREFIX}api/applet/staffCard/get_show_flag`, {
    params,
  });
};

/**
 * 展示名片开关 白名单企业
 * 江阴,宁夏,台州白名单银行支持分享时控制经理名片与顶部的展示
 * https://yapi.tengmoney.com/project/13/interface/api/8311
 */
export const getIsBankWhiteApi = async (): Promise<ApiResult<any>> => {
  return await httpClient.get(`${API_PREFIX}api/applet/staffCard/is_show_card_white_corp`);
};

/**
 * 七日年化分页接口
 * https://yapi.tengmoney.com/project/13/interface/api/2827
 */
export const getLatestWeeklyDataApi = async (
  params: MfNetValueLatestWeeklyYieldsRequest
): Promise<MfNetValueLatestWeeklyYieldsResponse> => {
  return await httpClient.get(`${API_PREFIX}api/applet/mfNetValue/latest_weekly_yields`, { params });
};

/**
 * 沪深300指数跌涨幅统计数据
 * https://yapi.tengmoney.com/project/13/interface/api/2822
 */
export const getCsi300RateDataApi = async (
  params: MfNetValueRestoredCsi300RateStatisticRequest
): Promise<MfNetValueRestoredCsi300RateStatisticResponse> => {
  return await httpClient.get(`${API_PREFIX}api/applet/mf_net_value_restored/csi300_rate_statistic`, { params });
};

/**
 * 南海定制-同类平均
 * https://yapi.tengmoney.com/project/13/interface/api/27557
 */
export const getCustomizationSimilarAverageApi = async (params: any): Promise<ApiResult<any>> => {
  return await httpClient.get(`${API_PREFIX}api/finance/customization/fund_detail/similar_average`, { params });
};

/**
 * 万份收益，七日年化
 */
export const getMfNetValueListApi = async (params: any): Promise<ApiResult<any>> => {
  return await httpClient.post(`${API_PREFIX}api/applet/mfNetValue/list.do`, params, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

/**
 * 万份收益，七日年化
 */
export const getYieldTrendListApi = async (params: any): Promise<ApiResult<any>> => {
  return await httpClient.get(`${API_PREFIX}api/applet/mf_net_value_restored/trends`, { params });
};

/**
 * 获取对应产品下的子栏目
 * https://yapi.tengmoney.com/project/13/interface/api/3742
 */
export const getProductCustomColumnApi = async (
  params: ProductListProductColumnInfoRequest
): Promise<ProductListProductColumnInfoResponse> => {
  return await httpClient.post(`${API_PREFIX}api/applet/product/list_product_column_info`, params, {
    headers: { isHideError: true },
  });
};

/**
 * 获取素材列表: 海报
 * https://yapi.tengmoney.com/project/13/interface/api/24333
 */
export const getProductPosterListApi = async (params: ListPosterRequest): Promise<ListPosterResponse> => {
  return await httpClient.get(`${API_PREFIX}api/applet/product/material/list/poster`, { params });
};

/**
 * 获取素材列表: 文章
 * https://yapi.tengmoney.com/project/13/interface/api/24341
 */
export const getProductNewsListApi = async (params: ListNewsRequest): Promise<ListNewsResponse> => {
  return await httpClient.get(`${API_PREFIX}api/applet/product/material/list/news`, { params });
};

/**
 * 获取素材列表: 视频
 * https://yapi.tengmoney.com/project/13/interface/api/24345
 */
export const getProductVideoListApi = async (params: ListVideoRequest): Promise<ListVideoResponse> => {
  return await httpClient.get(`${API_PREFIX}api/applet/product/material/list/video`, { params });
};

/**
 * pdf列表
 * https://yapi.tengmoney.com/project/13/interface/api/2937
 */
export const getProductPdfListApi = async (params: FundManualRequest): Promise<FundManualResponse> => {
  return await httpClient.get(`${API_PREFIX}api/applet/fund/manual`, { params });
};

/**
 * 更新海报可见
 * https://yapi.tengmoney.com/project/13/interface/api/24349
 */
export const updateProductPosterVisibleApi = async (params: VisiblePosterRequest): Promise<VisiblePosterResponse> => {
  return await httpClient.post(`${API_PREFIX}api/applet/product/material/update/visible/poster`, params);
};

/**
 * 更新文章可见
 * https://yapi.tengmoney.com/project/13/interface/api/24353
 */
export const updateProductNewsVisibleApi = async (params: VisibleNewsRequest): Promise<VisibleNewsResponse> => {
  return await httpClient.post(`${API_PREFIX}api/applet/product/material/update/visible/news`, params);
};

/**
 * 更新视频可见
 * https://yapi.tengmoney.com/project/13/interface/api/24357
 */
export const updateProductVideoVisibleApi = async (params: VisibleVideoRequest): Promise<VisibleVideoResponse> => {
  return await httpClient.post(`${API_PREFIX}api/applet/product/material/update/visible/video`, params);
};

/**
 * 话术列表
 * https://yapi.tengmoney.com/project/13/interface/api/788
 */
export const getProductSentenceListApi = async (
  params: ProductListSpeechRequest
): Promise<ProductListSpeechResponse> => {
  return await httpClient.post(`${API_PREFIX}api/applet/product/list_speech.do`, params);
};

/**
 * 常见问题列表
 * https://yapi.tengmoney.com/project/13/interface/api/788
 */
export const getProductQuestionListApi = async (params: ProductListIssueRequest): Promise<ProductListIssueResponse> => {
  return await httpClient.post(`${API_PREFIX}api/applet/product/list_issue.do`, params);
};

/**
 * 基金pdf预览
 * https://yapi.tengmoney.com/project/13/interface/api/788
 */
export const getProductPdfDetailApi = async (
  params: ManualPreviewImgListRequest
): Promise<ManualPreviewImgListResponse> => {
  return await httpClient.get(`${API_PREFIX}api/applet/fund/manual/preview_img_list`, { params });
};

/**
 * 复制常见问题
 */
export const copyProductQuestionApi = async (params: any): Promise<ApiResult<any>> => {
  return await httpClient.post(`${API_PREFIX}api/applet/product/issue_copy.do`, params);
};

/**
 * 复制话术
 */
export const copyProductIssueApi = async (params: any): Promise<ApiResult<any>> => {
  return await httpClient.post(`${API_PREFIX}api/applet/product/speech_copy.do`, params);
};

/**
 * 获取issue列表
 */
export const getProductContactIssueList = async (params: any): Promise<ApiResult<any>> => {
  return await httpClient.get(`${API_PREFIX}api/applet/product/contact/issue/list`, { params });
};

/**
 * issue列表统计复制次数
 */
export const addProductContactIssueCopyCount = async (params: any): Promise<ApiResult<any>> => {
  return await httpClient.post(`${API_PREFIX}api/applet/product/contact/issue/copy_add`, params);
};

/**
 * 产品详情-联系我-是否有联系我模块
 * https://yapi.tengmoney.com/project/13/interface/api/24809
 */
export const checkIsHasChatRightApi = async (params: ConfigValueRequest): Promise<ConfigValueResponse> => {
  return await httpClient.post(`${API_PREFIX}api/applet/product/config/value`, params);
};

/**
 * 产品详情-联系我-产品是否配置问题
 * https://yapi.tengmoney.com/project/13/interface/api/24433
 */
export const checkIsHasChatListApi = async (params: any): Promise<IssueListResponse> => {
  return await httpClient.get(`${API_PREFIX}api/applet/product/contact/issue/exists`, { params });
};

/**
 * 前往办理加密 业绩归属
 */
export const performanceEncryptionApi = async (params: any): Promise<any> => {
  return await httpClient.post(`${API_PREFIX}api/applet/performance/encryption`, params);
};

/**
 * 生成小程序码
 */
export const linkAppletCodeApi = async (params: any): Promise<any> => {
  return await httpClient.post(`${API_PREFIX}api/applet/product/zx/link_applet/code`, params);
};

/**
 *  海报进入产品详情 客户扫码埋点
 */
export const posterToProductRecordApi = async (params: any): Promise<any> => {
  return await httpClient.post(`${API_PREFIX}api/applet/poster/client_open_poster`, params, {
    headers: { isHideError: true },
  });
};

/**
 *  sop基金阅读埋点
 */
export const readFundInfoApi = async (params: any): Promise<any> => {
  return await httpClient.post(`${API_PREFIX}api/sop/snapshot/read_fund_info`, params, {
    headers: { isHideError: true },
  });
};

/**
 * sop基金收益提醒埋点
 */
export const readRemindFundInfoApi = async (params: any): Promise<any> => {
  return await httpClient.post(`${API_PREFIX}api/sop/snapshot/read_remind_fund_info`, params, {
    headers: { isHideError: true },
  });
};

/**
 * 成交意向客户统计表
 * https://yapi.tengmoney.com/project/13/interface/api/27321
 */
export const getIntentionCustomerReport = async (params: any): Promise<any> => {
  return await httpClient.get(`${API_PREFIX}data/home/intention-customer/report`, { params });
};

/**
 * 成交意向客户产品列表
 * https://yapi.tengmoney.com/project/13/interface/api/27325
 */
export const getIntentionCustomerListProduct = async (params: any): Promise<any> => {
  return await httpClient.post(`${API_PREFIX}data/home/intention-customer/list-product`, params);
};

/**
 * 成交意向客户列表
 * https://yapi.tengmoney.com/project/13/interface/api/27329
 */
export const getIntentionCustomerListCustomer = async (
  params: IntentionCustomerListCustomerRequest
): Promise<IntentionCustomerListCustomerResponse> => {
  return await httpClient.post(`${API_PREFIX}data/home/intention-customer/list-customer`, params);
};

/**
 * 成交意向客户externalUserID列表
 * https://yapi.tengmoney.com/project/13/interface/api/27333
 */
export const getIntentionCustomerListUserId = async (params: any): Promise<any> => {
  return await httpClient.get(`${API_PREFIX}data/home/intention-customer/list-user-id`, { params });
};

/**
 * 获取产品主要信息
 * https://yapi.tengmoney.com/project/13/interface/api/8735
 */
export const getProductMainInfo = async (params: any): Promise<any> => {
  return await httpClient.post(`${API_PREFIX}api/applet/product/get_main_info.do`, params);
};

/**
 * 通过主记录id获取预约申请详情
 * https://yapi.tengmoney.com/project/13/interface/api/26053
 */
export const getReservationData = async (params: any): Promise<IssueListResponse> => {
  return await httpClient.get(`${API_PREFIX}api/applet/reservation/reserve_data`, { params });
};

/**
 * 获取机构是否开通产品理财模板业绩走势
 * https://yapi.tengmoney.com/mock/13/caizhi_miniapi/api/applet/product/performance/trend/enable
 */
export const getProductTrendEnable = async (): Promise<ApiResult<any>> => {
  return await httpClient.get(`${API_PREFIX}api/applet/product/performance/trend/enable`);
};

/**
 * 查询理财模板业绩走势列表
 * https://yapi.tengmoney.com/mock/13/caizhi_miniapi/api/applet/product/performance/trend/list
 * @params productId: string;  产品 id
 * @params period: string;   WEEK：近一周  MONTH：近一月  QUARTER：近三月   ANNUAL：近一年
 */
export const getProductTrendList = async (params: { productId: string; period: string }): Promise<ApiResult<any>> => {
  return await httpClient.get(`${API_PREFIX}api/applet/product/performance/trend/list`, { params });
};

// 推产品-获取热推产品列表
export const getHotListApi = async (params: any): Promise<IssueListResponse> => {
  return await httpClient.get(`${API_PREFIX}api/applet/product/list_recommend.do`, { params });
};

// 产品池下产品列表（登录员工不同看到的产品列表由于可见范围不一不同）
export const getProductPoolListApi = async (params: any): Promise<IssueListResponse> => {
  return await httpClient.get(`${API_PREFIX}api/applet/product/pool/product_list`, { params });
};

// 主推处产品系列页签列表
export const getProductPoolTabListApi = async (params: any): Promise<any> => {
  return await httpClient.post(`${API_PREFIX}api/applet/product/pool/list`, params);
};

// 推产品-根据业务类型获取产品列表
export const productSearchList = async (params: any): Promise<any> => {
  return await httpClient.post(`${API_PREFIX}api/applet/product/search.do`, params);
};

// 类别产品列表 - 根据类别查询主推列表
export const getProductMainPushList = async (params: any): Promise<any> => {
  return await httpClient.post(`${API_PREFIX}api/applet/product/list-push-products`, params);
};

// 首发基金列表
export const getInitialProductList = async (params: any): Promise<any> => {
  return await httpClient.get(`${API_PREFIX}api/applet/fund/new_product_launch`, { params });
};

// 获取产品列表，目前应该只有江阴农商行用到
export const getAllProductList = async (params: any): Promise<any> => {
  return await httpClient.post(`${API_PREFIX}api/applet/product/list_all_product`, params);
};
